import { forwardRef, useCallback } from 'react';
import clsx from 'clsx';
import { Flex, TextArea } from '@nula/core-ui';
import { AssistantContextBar } from './assistant-context-bar';
import { SendStopButton } from './send-stop-button';
import type { UseAssistantContextReturn } from '../hooks/use-assistant-context';
import styles from './assistant-input.module.css';

interface AssistantInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  isRunActive: boolean;
  assistantContext: UseAssistantContextReturn;
  /**
   * Render the document-context picker row above the textarea. Defaults to
   * true (overlay/sidebar/full-tab). The HUD sets this false — the recording's
   * notes doc is pinned implicitly, so an explicit picker is redundant there.
   */
  showContextBar?: boolean;
  /** Extra CSS class merged onto the root wrapper (for chrome: bg, border, z-index) */
  className?: string;
}

export const AssistantInput = forwardRef<HTMLTextAreaElement, AssistantInputProps>(
  function AssistantInput(
    { value, onChange, onSend, onStop, isRunActive, assistantContext, showContextBar = true, className },
    ref,
  ) {
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
      if (e.key === 'Escape' && isRunActive) {
        onStop();
      }
    }, [onSend, onStop, isRunActive]);

    return (
      <Flex vertical className={clsx(styles.root, className)}>
        {showContextBar && (
          <AssistantContextBar className={styles.contextRow} {...assistantContext} isRunActive={isRunActive} />
        )}
        <TextArea
          ref={ref}
          variant="glass"
          className={styles.textarea}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Nula"
          aria-label="Message input"
          autoSize={{ minRows: 1, maxRows: 5 }}
          rows={1}
          action={
            <SendStopButton
              isRunActive={isRunActive}
              onSend={onSend}
              onStop={onStop}
              disabled={!value.trim() && !isRunActive}
            />
          }
        />
      </Flex>
    );
  },
);
